/**
 * Session lifecycle for web admin telemetry.
 *
 * Mirrors the Flutter TelemetryService session pattern:
 *   - Generate a stable session_token (UUID) on first boot, persist for
 *     re-renders within the same tab.
 *   - POST /telemetry/sessions on init.
 *   - POST /telemetry/sessions/:token/end on `beforeunload` AND on the
 *     `pagehide` event (Safari/iOS fires pagehide but not beforeunload
 *     when the tab is bfcache-frozen).
 *
 * `fetch(..., { keepalive: true })` in api.ts is what makes the end-ping
 * survive the tab teardown — a normal fetch is aborted when the document
 * unloads.
 *
 * Retry queue: end-pings that fail (network down, 5xx, dropped keepalive)
 * are stashed in localStorage under PENDING_KEY before the fetch fires.
 * The next `startSession` flushes the queue. Backend is idempotent on
 * end-calls so worst-case duplicates are harmless.
 */

import { postSessionStart, postSessionEnd } from './api'

let sessionToken: string | null = null

const PENDING_KEY = 'tp_pending_session_ends'
/** Cap on the retry queue — a sustained outage shouldn't bloat localStorage.
 *  Oldest tokens are dropped first; the server still gets the most recent N. */
const MAX_PENDING = 20

function getPending(): string[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'string') : []
  } catch { return [] }
}

function setPending(tokens: string[]): void {
  try {
    if (tokens.length === 0) localStorage.removeItem(PENDING_KEY)
    else localStorage.setItem(PENDING_KEY, JSON.stringify(tokens))
  } catch { /* storage blocked or quota exhausted — drop silently */ }
}

function enqueuePending(token: string): void {
  const pending = getPending()
  if (pending.includes(token)) return
  pending.push(token)
  while (pending.length > MAX_PENDING) pending.shift()
  setPending(pending)
}

function dequeuePending(token: string): void {
  setPending(getPending().filter((t) => t !== token))
}

async function flushPending(): Promise<void> {
  const pending = getPending()
  if (pending.length === 0) return
  const stillPending: string[] = []
  for (const token of pending) {
    const done = await postSessionEnd(token)
    if (!done) stillPending.push(token)
  }
  setPending(stillPending)
}

function detectDevice(): string {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua))      return 'Edge / ' + navigator.platform
  if (/Chrome\//.test(ua))   return 'Chrome / ' + navigator.platform
  if (/Firefox\//.test(ua))  return 'Firefox / ' + navigator.platform
  if (/Safari\//.test(ua))   return 'Safari / ' + navigator.platform
  return 'web-admin'
}

function genToken(): string {
  // crypto.randomUUID() is universal in modern browsers; the conditional
  // below keeps us safe in test environments that stub `crypto`.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'sess-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36)
}

export async function startSession(): Promise<void> {
  if (sessionToken) return
  // Best-effort flush of end-pings from prior tabs that failed mid-flight.
  // Fire-and-forget so a slow retry doesn't block this boot's start call.
  void flushPending()

  const initial = genToken()
  sessionToken = await postSessionStart(initial, detectDevice())

  // End-of-session pings. Two events cover the two ways a tab really
  // ends: `pagehide` (BFCache, iOS Safari) and `beforeunload` (desktop
  // close/refresh). Both fire-and-forget via keepalive.
  const onEnd = () => endSession()
  window.addEventListener('beforeunload', onEnd)
  window.addEventListener('pagehide', onEnd)
}

export function endSession(): void {
  if (!sessionToken) return
  const token = sessionToken
  sessionToken = null
  // Stash BEFORE firing so a dropped keepalive ping (network down, tab
  // killed mid-request) survives the teardown and gets retried next boot.
  enqueuePending(token)
  // Don't await — pagehide handlers run synchronously and the response
  // doesn't matter for this tab. The .then() may not run during teardown;
  // that's OK because the backend is idempotent on a second end-call.
  void postSessionEnd(token).then((done) => { if (done) dequeuePending(token) })
}

export function currentSessionToken(): string | null {
  return sessionToken
}
